<template>
    <div class="field">
        <div v-if="multipleChoice" class="columns">
            <div class="column">
                <button
                    class="button is-primary is-outlined is-pulled-right"
                    @click="$emit('add-answer')"
                    @keyup="$emit('add-answer')"
                >
                    Add Answer
                </button>
            </div>
        </div>

        <div class="columns content">
            <div class="column is-10">
                <div v-if="details.type !== 'free_form'" class="has-text-weight-bold">Answers</div>
            </div>

            <div v-if="!survey && !$isMobile" class="column is-2 has-text-centered">
                <div class="has-text-weight-bold">Correct</div>
            </div>
        </div>

        <div style="display: grid">
            <draggable
                v-bind="dragOptions"
                :list="answers"
                tag="transition-group"
                :component-data="{ type: 'transition', class: 'quiz-answers-container' }"
                handle=".media-draggable__draghandle"
                @end="handleDrop"
            >
                <template #item="{ element: answer }">
                    <div
                        class="columns is-vcentered"
                        :style="$isMobile ? 'display: flex; flex-direction: column-reverse;' : 'margin-bottom: 0;'"
                    >
                        <div class="column is-12-touch" :class="survey ? 'is-12' : 'is-10'">
                            <!-- TODO: fix weird dragging highlights due to nesting -->
                            <quiz-answer
                                :key="`${details.parent}-${answer.display_order}`"
                                :answer="answer"
                                :uuid="details.uuid"
                                :type="details.type"
                            />
                        </div>

                        <div v-if="!survey" class="tw:flex tw:w-full tw:justify-center">
                            <e-radio
                                v-if="!multipleAnswer"
                                v-model="correct"
                                :native-value="answer.display_order"
                            />

                            <e-checkbox v-else v-model="correct" :native-value="answer.display_order" />
                        </div>
                    </div>
                </template>
            </draggable>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

import QuizAnswer from '@/components/quiz-builder/quiz-answers/quiz-answer/QuizAnswer.vue';
import QuizTypeMixin from '@/mixins/QuizType.mixin.js';

export default {
    name: 'QuizAnswers',

    components: { draggable, QuizAnswer },

    mixins: [QuizTypeMixin],

    props: {
        answers: {
            type: Array,
            required: true,
        },

        details: {
            type: Object,
            required: true,
        },

        multipleChoice: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['add-answer'],

    data: () => ({
        dragOptions: {
            animation: 200,
            group: 'answers',
            disabled: false,
            ghostClass: 'is-ghost',
        },

        correct: [],

        onLoad: true,
    }),

    computed: {
        multipleAnswer() {
            return this.details.type.includes('answer');
        },

        uuid() {
            return this.details.uuid;
        },

        columnSize() {
            return this.survey ? 'is-12' : 'is-10';
        },
    },

    watch: {
        correct() {
            if (!this.onLoad) {
                if (this.multipleAnswer) {
                    const payload = this.correct ? this.getCorrectAnswers() : this.correct;

                    this.set(payload);
                }

                if (!this.multipleAnswer) this.set(this.answers[this.correct]);
            }

            this.onLoad = false;
        },
    },

    created() {
        this.setAnswers();
    },

    methods: {
        // Helper function to find answer by display order
        findAnswerByDisplayOrder(displayOrder) {
            return this.answers.findIndex(answer => answer.display_order === displayOrder);
        },

        // Helper function to collect correct answers for multiple answer types
        collectCorrectAnswers() {
            const result = [];

            this.correct.forEach(item => {
                const index = this.findAnswerByDisplayOrder(item);
                if (index >= 0) {
                    result.push(this.answers[index]);
                }
            });

            return result;
        },

        // Helper function to find single correct answer
        findSingleCorrectAnswer() {
            const index = this.answers.findIndex(answer => answer.is_correct);
            return index >= 0 ? this.answers[index].display_order : null;
        },

        // Helper function to collect multiple correct answers
        collectMultipleCorrectAnswers() {
            const correctAnswers = [];

            this.answers.forEach(answer => {
                if (answer.is_correct) {
                    correctAnswers.push(answer.display_order);
                }
            });

            return correctAnswers;
        },

        getCorrectAnswers() {
            return this.collectCorrectAnswers();
        },

        handleDrop() {
            this.onLoad = true;

            this.$store.dispatch(`QuizBuilder/${this.details.uuid}/reorder`, {
                field: 'display_order',
                parent: this.details.parent,
                payload: this.answers,
                type: 'answers',
            });

            this.setAnswers();
        },

        set(payload) {
            this.$store.dispatch(`QuizBuilder/${this.details.uuid}/setCorrectAnswer`, {
                payload,
                type: this.details.type,
                parent: this.details.parent,
            });
        },

        setAnswers() {
            let correctAnswers;

            if (!this.multipleAnswer) {
                correctAnswers = this.findSingleCorrectAnswer();
            }

            if (this.multipleAnswer) {
                correctAnswers = this.collectMultipleCorrectAnswers();
            }

            if (correctAnswers) {
                this.correct = correctAnswers;
            } else {
                this.onLoad = false;
            }
        },
    },

    // Consolidated emits declaration at the top of the component
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.quiz-answers-container {
    display: block;
    margin-bottom: -$column-gap;
}
</style>
